import React, { useEffect, useRef, useState } from 'react';
import Wizard from '../../components/wizard';
import CustomInput from '../../components/customInput';
import CustomTextArea from '../../components/customTextArea';
import CustomDropdown from '../../components/customDropdown';
import DropdownSearch from '../../components/dropdownSearch';
import Loading from '../../components/loading';
import Locations from '../sites/siteSteps/locations';
import '../sites/siteSteps/styles.scss';
import API from '../../services/api';
import {
  ATTRIBUTION_WINDOWS, newTargetSite, retailSiteType, validSettings, validLocation, validAttribution, targetSiteRequest
} from '../../utils/storeAttribution/creation';

const loadPages = async (load, field) => {
  const items = [];
  const visited = new Set();
  let token = '';
  do {
    if (visited.has(token)) throw new Error('Repeated page token');
    visited.add(token);
    const response = await load(token);
    items.push(...(response[field] || []));
    token = response.nextPageToken || '';
  } while (token);
  return items;
};

const CreateTargetSites = ({ portal, onCancel, onCreated }) => {
  const [target, setTarget] = useState(newTargetSite());
  const [types, setTypes] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    const filter = encodeURI(`brandPortalId = "${portal.id}"`);
    Promise.all([
      loadPages(token => API.getSiteTypes(token), 'siteTypes'),
      loadPages(token => API.getCampaigns(portal.companyId, token, filter), 'campaigns')
    ]).then(([siteTypes, portalCampaigns]) => {
      if (!active) return;
      setTypes(siteTypes);
      setCampaigns(portalCampaigns.map(campaign => ({ value: campaign.id, label: campaign.displayName || campaign.id })));
      const retail = retailSiteType(siteTypes);
      setTarget(current => ({ ...current, type: current.type || retail || null }));
    }).catch(() => {
      if (active) setLoadError('Could not load site types and campaigns. Please try again.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [portal.id, portal.companyId, retry]);

  const selected = target;
  const updateTarget = setTarget;

  const submit = async () => {
    if (savingRef.current || !portal.id || !portal.companyId || !validSettings(target) || !validLocation(target) ||
      !validAttribution(target, campaigns)) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
      await API.createTargetSite(portal.id, targetSiteRequest(target, portal.id, portal.companyId));
      if (mounted.current) onCreated();
    } catch (error) {
      if (mounted.current) setSaveError('Target sites could not be created. Your entries have been preserved. Please try again.');
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const steps = [
    {
      title: 'Settings', required: true, hideDescription: true,
      validation: () => Boolean(portal.id && portal.companyId) && validSettings(target),
      content: (
        <>
          <div className="form-group">
            <label>Name*</label>
            <CustomInput id="target-name" isEditable value={selected.displayName} placeholder="Write the name"
              onChange={event => updateTarget({ ...selected, displayName: event.target.value })}
              errorMessage={selected.displayName.trim().length < 4 ? 'Enter at least 4 characters.' : ''} />
          </div>
          <div className="form-group">
            <DropdownSearch label="Type*" selected={selected.type && { value: selected.type.id, label: selected.type.displayName }}
              options={types.map(type => ({ value: type.id, label: type.displayName }))}
              onSelect={id => updateTarget({ ...selected, type: types.find(type => type.id === id) })} leftAlign />
          </div>
          <div className="form-group description-field">
            <label>Description</label>
            <CustomTextArea isEditable value={selected.description}
              onChange={event => updateTarget({ ...selected, description: event.target.value })} />
          </div>
        </>
      )
    },
    {
      title: 'Location', required: true,
      validation: () => validLocation(target),
      content: <Locations site={selected} updateSite={updateTarget} createMode />
    },
    {
      title: 'Attribution Window', required: true,
      validation: () => validAttribution(target, campaigns),
      submitButtonTitle: 'Create Target Site',
      content: (
        <>
          {campaigns.length === 0 && <p role="status">This portal has no campaigns available for attribution.</p>}
          <div className="form-group">
            <DropdownSearch label="Campaign*" selected={campaigns.find(campaign => campaign.value === selected.campaignId)}
              options={campaigns} onSelect={campaignId => updateTarget({ ...selected, campaignId })} leftAlign />
          </div>
          <div className="form-group">
            <CustomDropdown label="Attribution Window" items={ATTRIBUTION_WINDOWS} valueField="id" displayField="label"
              selectedItem={selected.attributionWindowDays}
              handleSelection={(event, index) => updateTarget({ ...selected, attributionWindowDays: ATTRIBUTION_WINDOWS[index].id })} />
          </div>
          {saveError && <p role="alert" className="text-danger">{saveError}</p>}
        </>
      )
    }
  ];

  if (loading || loadError) return (
    <div className="list-message">
      {loading ? <Loading size={8} /> : <><p role="alert">{loadError}</p>
        <button className="btn btn-secondary" onClick={() => setRetry(value => value + 1)}>Try again</button></>}
      <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
    </div>
  );

  return (
    <div className={`sites site-creation target-site-creation ${saving ? 'saving' : ''}`} aria-busy={saving}>
      <fieldset disabled={saving}>
      <Wizard title="Create Target Site" icon="store"
        steps={steps} onCancel={() => !savingRef.current && onCancel()} onSubmit={submit} />
      </fieldset>
    </div>
  );
};

export default CreateTargetSites;
